import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { ApiError, api, sessionEvents } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import type { SessionUser } from '@/api/types';
import { Icon } from '@/components/Icon';
import { useSession } from '@/stores/session';
import { NAV, canSeeNavItem } from './nav';
import s from './SessionGate.module.css';

function landingFor(user: SessionUser | null) {
  if (!user) return '/me';
  const can = (...perms: string[]) => perms.some((p) => user.permissions.includes(p));
  const allowed = (key: string) => NAV.find((item) => item.key === key && item.to && canSeeNavItem(item, can, user.role))?.to;
  return allowed('home') || allowed('settlements') || allowed('inventory') || NAV.find((item) => item.to && canSeeNavItem(item, can, user.role))?.to || '/me';
}

const FEATURES = [
  { icon: 'book', title: '产品画册', text: '规格、包装方案、图纸一处查' },
  { icon: 'box', title: '套装自动展开', text: '按每套 / 每组用量算到组件' },
  { icon: 'lock', title: '多产品预占', text: '提交即锁库存，缺口进采购建议' },
  { icon: 'truck', title: '分批发货', text: '物流单记录，破损补耗单独记' },
];

/** 登录与重登共用原有账号服务和会话 cookie；只在 V2 绘制表单。 */
function LoginForm({ expired = false }: { expired?: boolean }) {
  const ff = useFormFactor();
  const user = useSession((st) => st.user);
  const load = useSession((st) => st.load);
  const nav = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [blockedIp, setBlockedIp] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const [focusedField, setFocusedField] = useState<'username' | 'password' | null>(null);
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const retrySeconds = Math.max(0, Math.ceil((retryAt - clock) / 1000));

  useEffect(() => {
    if (!retryAt || retryAt <= Date.now()) return;
    const timer = window.setInterval(() => {
      const now = Date.now();
      setClock(now);
      if (now >= retryAt) window.clearInterval(timer);
    }, 250);
    return () => window.clearInterval(timer);
  }, [retryAt]);
  useEffect(() => { if (expired) passwordRef.current?.focus(); }, [expired]);
  useEffect(() => {
    if (ff !== 'phone') return;
    const viewport = window.visualViewport;
    const update = () => setKeyboardInset(viewport ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0);
    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    return () => { viewport?.removeEventListener('resize', update); viewport?.removeEventListener('scroll', update); };
  }, [ff]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || blockedIp || retrySeconds) return;
    const account = expired ? user?.username || '' : username.trim();
    if (!account || !password) {
      setMessage('请输入账号和密码。');
      if (!password) passwordRef.current?.focus();
      return;
    }
    setBusy(true);
    setMessage('');
    setInvalidCredentials(false);
    try {
      await api.login(account, password);
      await load();
      const session = useSession.getState();
      if (session.status !== 'authed') throw new Error('登录成功，但无法读取账号权限，请重试。');
      setPassword('');
      sessionEvents.dispatchEvent(new Event('restored'));
      if (!expired) nav(location.pathname === '/' ? landingFor(session.user) : `${location.pathname}${location.search}`, { replace: true });
    } catch (error) {
      const apiError = error instanceof ApiError ? error : null;
      if (apiError?.status === 429) {
        const seconds = Number(apiError.message.match(/(\d+)\s*秒/)?.[1] || 60);
        setRetryAt(Date.now() + seconds * 1000);
        setClock(Date.now());
        setMessage(apiError.message);
      } else if (apiError?.status === 403) {
        setBlockedIp(true);
        setMessage(apiError.message);
      } else if (apiError?.status === 401) {
        setInvalidCredentials(true);
        setMessage('账号或密码不正确，请检查后重新输入。连续输错多次会被暂时锁定。');
      } else setMessage(error instanceof Error ? error.message : '登录失败，请重试。');
      setPassword('');
      requestAnimationFrame(() => passwordRef.current?.focus());
    } finally { setBusy(false); }
  };

  const fields = (
    <form onSubmit={submit} className={s.form}>
      {message && <div className={s.error} role="alert">{invalidCredentials ? <><Icon name="alert" size={16} /><span><b>账号或密码不正确</b><small>请检查后重新输入。连续输错多次会被暂时锁定。</small></span></> : message}</div>}
      {expired ? (
        <div className={`${s.input} ${s.locked}`}>
          <span className={s.avatar}>{user?.name?.slice(0, 1) || '我'}</span>
          <span>{user?.name || '当前账号'} · {user?.username || '—'}</span>
          <Icon name="lock" size={14} />
        </div>
      ) : (
        <label className={s.input}>
          <Icon name="user" size={20} />
          <input ref={usernameRef} name="username" aria-label="账号" autoComplete="username" value={username} onFocus={() => setFocusedField('username')} onBlur={(event) => { const target = event.relatedTarget; if (!(target instanceof HTMLElement) || (!target.closest('[data-keyboard-toolbar]') && !target.matches('input'))) setFocusedField(null); }} onChange={(e) => setUsername(e.target.value)} placeholder="账号" required />
        </label>
      )}
      <label className={`${s.input} ${invalidCredentials ? s.invalid : ''}`}>
        <Icon name={expired ? 'key' : 'lock'} size={20} />
        <input ref={passwordRef} name="password" aria-label="密码" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onFocus={() => setFocusedField('password')} onBlur={(event) => { const target = event.relatedTarget; if (!(target instanceof HTMLElement) || (!target.closest('[data-keyboard-toolbar]') && !target.matches('input'))) setFocusedField(null); }} onChange={(e) => setPassword(e.target.value)} placeholder={expired ? '输入密码' : '密码'} required />
        <button type="button" className={s.eye} onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? '隐藏密码' : '显示密码'}><Icon name={showPassword ? 'eye' : 'eyeoff'} size={20} /></button>
      </label>
      {!expired && <button type="submit" className={s.submit} disabled={busy || blockedIp || retrySeconds > 0}>{busy ? '登录中…' : retrySeconds ? `${retrySeconds} 秒后可重试` : '登录'}</button>}
      {!expired && <div className={s.hint}>忘记密码请联系管理员重置</div>}
      {expired && <div className={s.sheetActions}>
        <button type="button" className={s.switch} onClick={() => useSession.setState({ user: null, status: 'anon' })}>换个账号</button>
        <button type="submit" className={s.submit} disabled={busy || blockedIp || retrySeconds > 0}>{busy ? '登录中…' : retrySeconds ? `${retrySeconds} 秒后可重试` : '登录并继续'}</button>
      </div>}
    </form>
  );

  if (expired) return (
    <div className={s.sheet} role="dialog" aria-modal="true" aria-labelledby="expired-title">
      <header><h1 id="expired-title">登录已过期</h1><small>重新验证后回到这一页</small></header>
      <div className={s.sheetBody}>
        {ff === 'phone' ? (
          <div className={s.preserved} role="status"><Icon name="info" size={16} /><span><b>你填的内容还在</b><small>当前页面的本机暂存内容会在重新登录后恢复，不用重填。</small></span></div>
        ) : (
          <p>{ff === 'tablet' ? '这台 iPad 上的登录已满 7 天。重新输入密码后回到当前页面，列表位置和打开的订单详情保持；之前失效的查询自动重发，提交类不会自动重试。' : '当前页面的内容已保留，重新登录后回到这一页。'}</p>
        )}
        {fields}
        <p className={s.hint}>如账号已停用或密码已更改，请联系管理员。</p>
      </div>
    </div>
  );

  return (
    <div className={`${s.login} ${ff === 'phone' && !expired && focusedField !== null ? s.phoneKeyboardOpen : ''}`}>
      <section className={s.hero}>
        <div className={s.logo}>发</div>
        <h1>贸易商进销存系统</h1>
        <p>多 SKU 订单 · 预占库存 · 分批发货</p>
        <ul>{FEATURES.map((feature) => <li key={feature.title}><span><Icon name={feature.icon} size={20} /></span><div><b>{feature.title}</b><small>{feature.text}</small></div></li>)}</ul>
        <div className={s.heroLegal}>v2.0.0 · 仅限公司内部使用</div>
      </section>
      <section className={s.loginSide}>
        <div className={s.card}>
          <div className={s.cardTitle}><h2>登录</h2><p>用管理员给你开的账号登录。</p></div>
          {fields}
          <p className={s.keyboard}><Icon name="kbd" size={14} />外接键盘：<kbd>tab</kbd> 切换输入框，<kbd>↵</kbd> 登录</p>
        </div>
        <p className={s.mobileLegal}>这台设备会保持登录 7 天<br />v2.0.0 · 仅限公司内部使用</p>
      </section>
      {ff === 'phone' && !expired && focusedField !== null && <div className={s.keyboardToolbar} style={{ bottom: keyboardInset }} role="toolbar" aria-label="登录字段导航" data-keyboard-toolbar>
        <button type="button" disabled={focusedField === 'username'} onClick={() => usernameRef.current?.focus()}>上一项</button>
        <button type="button" className={s.doneKey} onClick={() => { (focusedField === 'username' ? usernameRef.current : passwordRef.current)?.blur(); setFocusedField(null); }}>完成</button>
      </div>}
    </div>
  );
}

export function SessionGate() {
  const status = useSession((st) => st.status);
  const load = useSession((st) => st.load);
  const error = useSession((st) => st.error);
  useEffect(() => { void load(); }, [load]);
  if (status === 'loading') return <div className={s.boot}><span className={s.spin} /></div>;
  if (status === 'anon') return <LoginForm />;
  if (status === 'error') return <div className={s.serverError}><h1>连不上服务器</h1><p>{error}</p><button onClick={() => void load()}>重试</button></div>;
  return <>
    <Outlet />
    {status === 'expired' && <div className={s.overlay}><LoginForm expired /></div>}
  </>;
}
